/**
 * «Continue» of the lobby: the last game screen the player opened (any game) and the newest unfinished skill session
 * (a local save of a guest or a server session of a signed-in player). Per device, in localStorage; nothing here is
 * sent anywhere. Every read and write is guarded: private mode / blocked storage simply means «nothing to continue».
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { GAME_IDS, gamePathOf, isReleased, type GameId } from '@tg/shared';
import { isLocale } from '@/i18n/locales';

const KEY = 'tg.lastGame.v1';

export interface LastGame {
  game: GameId;
  /** epoch ms of the visit */
  at: number;
}

const PATHS = new Map<string, GameId>(GAME_IDS.map((g) => [gamePathOf(g), g]));

/** "/ru/slots/garden" → 'garden'; anything else → null */
export function gameOfPath(pathname: string): GameId | null {
  const seg = pathname.replace(/\/+$/, '').split('/');
  if (!isLocale(seg[1] ?? '')) return null;
  return PATHS.get('/' + seg.slice(2).join('/')) ?? null;
}

export function readLastGame(): LastGame | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<LastGame>;
    if (typeof v.game !== 'string' || !(GAME_IDS as readonly string[]).includes(v.game) || typeof v.at !== 'number') return null;
    return { game: v.game as GameId, at: v.at };
  } catch {
    return null;
  }
}

export function writeLastGame(game: GameId, at = Date.now()): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ game, at }));
  } catch {
    /* storage blocked: no «Continue» */
  }
}

/** Remembers the game of the current route (mounted once in App). Games behind «coming soon» are not remembered. */
export function useTrackLastGame(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const g = gameOfPath(pathname);
    if (g && isReleased(g)) writeLastGame(g);
  }, [pathname]);
}

/** The last released game opened on this device (null: none yet / storage blocked) */
export function useLastGame(): LastGame | null {
  const [v, setV] = useState<LastGame | null>(() => readLastGame());
  useEffect(() => {
    const on = (e: StorageEvent) => {
      if (e.key === KEY) setV(readLastGame());
    };
    window.addEventListener('storage', on);
    return () => window.removeEventListener('storage', on);
  }, []);
  return v && isReleased(v.game) ? v : null;
}

/** Renders nothing: remembers the game of the route (App). */
export function LastGameTracker(): null {
  useTrackLastGame();
  return null;
}
